import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { scoringConfigFor } from '@shared/game-logic';
import { Button, Panel, Screen } from '@/components/ui';
import GameSettings, { describeSettings, type GameSettingsValue } from '@/components/GameSettings';
import { useRules } from '@/store/rules';
import { ROOM_CODE_LENGTH, isValidRoomCode, normalizeRoomCode } from '@/live/roomCode';
import { seatFor } from '@/live/sessions';
import WhoAreYou, { type Chosen } from '@/components/WhoAreYou';

export default function JoinLive() {
  const navigate = useNavigate();
  const params = useParams<{ code?: string }>();

  /*
   * Why you are here, decided by how you got here.
   *
   * A code in the URL means a link or a QR from whoever is hosting: you came to
   * join, so joining goes first and the rules stay out of the way. No code means
   * you came from the home menu, and then starting a potje is what you want —
   * with the rules right there, above the button, rather than below it where the
   * only way to see them was to have already pressed it.
   */
  const arrivedToJoin = Boolean(params.code);

  const [code, setCode] = useState(params.code ? normalizeRoomCode(params.code) : '');
  const [me, setMe] = useState<Chosen>(() => ({
    name: seatFor(params.code ?? '')?.name ?? '',
    profileId: null,
  }));
  const name = me.name;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(!arrivedToJoin);

  // Whatever this phone last started a potje with. Written back on create.
  const remembered = useRules((state) => state.rules);
  const remember = useRules((state) => state.remember);
  const [settings, setSettings] = useState<GameSettingsValue>(remembered);

  const codeOk = isValidRoomCode(code);

  function join() {
    if (!codeOk || !name.trim()) return;
    navigate(`/live/${normalizeRoomCode(code)}`, {
      state: { name: name.trim(), profileId: me.profileId },
    });
  }

  async function createRoom() {
    if (!name.trim()) {
      setError('Vul eerst je naam in.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Every setting is sent explicitly. Posting an empty body here is what
      // once made a live game silently run on the server defaults, whatever the
      // table had chosen.
      const response = await fetch('/api/rooms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          maxCards: settings.maxCards,
          direction: settings.direction,
          repeatTopRound: settings.repeatTopRound,
          extraNoTrumpRound: settings.extraNoTrumpRound,
          noTrumpOnTheWayDown: settings.noTrumpOnTheWayDown,
          scoringPreset: settings.scoringPreset,
          scoring:
            settings.scoringPreset === 'eigen'
              ? settings.customScoring
              : scoringConfigFor(settings.scoringPreset),
          houseRules: settings.houseRules,
        }),
      });
      if (!response.ok) throw new Error(String(response.status));
      const { code: created } = (await response.json()) as { code: string };
      // Only once it is a real potje: an abandoned fiddle is not the house rules.
      remember(settings);
      navigate(`/live/${created}`, { state: { name: name.trim(), profileId: me.profileId } });
    } catch {
      setError('Kon geen potje aanmaken. Is er verbinding met de server?');
    } finally {
      setBusy(false);
    }
  }

  const joinBlock = (
    <Panel title="Meedoen met een potje">
      <input
        value={code}
        onChange={(event) =>
          setCode(normalizeRoomCode(event.target.value).slice(0, ROOM_CODE_LENGTH))
        }
        placeholder="Code"
        inputMode="text"
        autoCapitalize="characters"
        autoComplete="off"
        className="min-h-14 w-full rounded-xl border border-white/10 bg-base-900 px-3 text-center font-tabular text-2xl tracking-[0.4em] outline-none focus:border-accent-500"
      />
      <div className="mt-3">
        <Button
          variant={arrivedToJoin ? 'primary' : undefined}
          full
          onClick={join}
          disabled={!codeOk || !name.trim()}
        >
          Meedoen
        </Button>
      </div>
      <p className="mt-2 text-sm text-slate-500">
        De code van {ROOM_CODE_LENGTH} tekens krijg je van degene die het potje aanmaakte.
      </p>
    </Panel>
  );

  /*
   * The rules sit between the explanation and the button, so the last thing you
   * do on this screen is start a potje you have just seen the rules of.
   */
  const createBlock = (
    <>
      <Panel
        title={arrivedToJoin ? 'Of begin er zelf een' : 'Begin een potje'}
        action={
          <button
            type="button"
            onClick={() => setShowSettings((current) => !current)}
            className="text-sm text-accent-400 hover:underline"
          >
            {showSettings ? 'Regels verbergen' : 'Regels aanpassen'}
          </button>
        }
      >
        <p className="text-sm text-slate-500">
          Je krijgt een code die de anderen kunnen invoeren. Vanaf drie spelers kun je beginnen.
        </p>
        <p className="mt-2 text-sm text-slate-400">{describeSettings(settings)}</p>
        {!showSettings && (
          <p className="mt-1 text-xs text-slate-500">Zoals je het vorige potje speelde.</p>
        )}
      </Panel>

      {showSettings && (
        <GameSettings
          value={settings}
          onChange={(patch) => setSettings((current) => ({ ...current, ...patch }))}
          table={{ known: false }}
        />
      )}

      <Button
        variant={arrivedToJoin ? undefined : 'primary'}
        full
        onClick={createRoom}
        disabled={busy}
      >
        {busy ? 'Bezig…' : 'Nieuw live potje'}
      </Button>
    </>
  );

  return (
    <Screen title="Boerenbridge live" back="/" subtitle="Iedereen speelt vanaf zijn eigen telefoon">
      <Panel title="Wie ben je">
        <WhoAreYou value={me} onChange={setMe} />
      </Panel>

      {arrivedToJoin ? joinBlock : createBlock}
      {arrivedToJoin ? createBlock : joinBlock}

      {error && <p className="text-center text-sm text-bad">{error}</p>}
    </Screen>
  );
}
